// FILE: src/pages/admin/student/AdminStudentRecordsPage.jsx
// One student's full record, made by the server (WP-7; MTO 11.1, 11.4,
// matrix 5.6): PDF, CSV or JSON with a SHA-256 fingerprint, plus where the
// related reports are. Records of locked or ended enrolments download the
// same way: records are kept for 7 years after the course.
// The curriculum the student takes (MTO 11.3, WP-5) is part of the record:
// it shows after a JSON download, or on request (reading the record JSON is
// audited like any download, so it isn't fetched on every visit).
import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Link, Stack, Typography } from "@mui/material";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import fetchWithAuth from "../../../utils/fetchWithAuth";
import { DetailList, Section, StatusChip } from "../../../components/ui";
import RecordDownloads from "../../../components/admin/RecordDownloads";
import { formatDate, formatNumber } from "../../../components/admin/format";
import { errorMessage } from "../../../i18n/errors";
import { useStudent } from "./AdminStudentLayout";

const CURRICULUM_STATUS = {
  APPROVED: { label: "Approved", tone: "success", Icon: TaskAltOutlinedIcon },
  RETIRED: { label: "Retired: no new students", tone: "default", Icon: ArchiveOutlinedIcon },
  DRAFT: { label: "Draft", tone: "warning", Icon: EditNoteOutlinedIcon }
};

/** record.curriculum = { id, name, version, status, mtoApprovalRef, approvedAt, fixedAtRegistration, packageHashes[] } */
export function CurriculumDetails({ curriculum }) {
  if (!curriculum?.id) {
    return <Typography>No curriculum is recorded for this student yet.</Typography>;
  }
  const status = CURRICULUM_STATUS[curriculum.status];
  return (
    <Stack spacing={2}>
      {status && <StatusChip tone={status.tone} icon={<status.Icon />} label={status.label} />}
      <DetailList
        items={[
          { label: "Curriculum", value: curriculum.name || "—" },
          { label: "Version", value: curriculum.version != null ? String(curriculum.version) : "—" },
          { label: "MTO approval reference", value: curriculum.mtoApprovalRef || "Not given" },
          { label: "Approved on", value: curriculum.approvedAt ? formatDate(curriculum.approvedAt) : "Not approved" },
          {
            label: "Fixed at registration",
            value: curriculum.fixedAtRegistration ? "Yes" : "No: the current approved version applies"
          },
          {
            label: "Lessons fingerprinted",
            value: `${formatNumber((curriculum.packageHashes || []).length)} (SHA-256 of each lesson, frozen at approval)`
          }
        ]}
      />
    </Stack>
  );
}

const CONTENTS = [
  "Identity and registration: name, registration number, licence, enrolment dates",
  "Every sign-in, sign-out and failed attempt, and identity checks during lessons",
  "Credited learning time for each day (Ontario time), core and flex hours separate",
  "Learning sessions and breaks",
  "Each lesson's progress, completion date and scores",
  "Support requests with every message, who answered and their instructor registration",
  "Getting-started acknowledgements with document versions, and the course survey"
];

export default function AdminStudentRecordsPage() {
  const { student } = useStudent();
  const [curriculum, setCurriculum] = useState({ data: null, loading: false, error: null });

  const showCurriculum = async () => {
    setCurriculum({ data: null, loading: true, error: null });
    try {
      const json = await fetchWithAuth(`/api/admin/users/${encodeURIComponent(student._id)}/record.json`);
      setCurriculum({ data: json?.record?.curriculum || {}, loading: false, error: null });
    } catch (error) {
      setCurriculum({ data: null, loading: false, error: error || "INTERNAL_ERROR" });
    }
  };

  return (
    <Stack spacing={2.5}>
      <Section
        id="download-record"
        title="Download the record"
        description="The complete record the Ministry may ask for. Each download is recorded in the audit log."
      >
        <RecordDownloads
          userId={student._id}
          onRecord={(record) => setCurriculum({ data: record.curriculum || {}, loading: false, error: null })}
        />
      </Section>

      <Section
        id="curriculum-taken"
        title="Curriculum taken"
        description="The Ministry-approved curriculum version this student follows. Students finish on the version they registered with."
      >
        {curriculum.data ? (
          <CurriculumDetails curriculum={curriculum.data} />
        ) : (
          <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
            {curriculum.error && (
              <Alert severity="error" role="alert">
                {errorMessage(curriculum.error)}
              </Alert>
            )}
            <Typography sx={{ color: "text.secondary" }}>
              It's read from the student's record, so looking at it is recorded in the audit log like a download.
            </Typography>
            <Button
              variant="outlined"
              onClick={showCurriculum}
              disabled={curriculum.loading}
              startIcon={curriculum.loading ? <CircularProgress size={18} color="inherit" aria-hidden /> : undefined}
            >
              {curriculum.loading ? "Reading the record…" : "Show the curriculum"}
            </Button>
          </Stack>
        )}
      </Section>

      <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, alignItems: "start" }}>
        <Section id="record-contents" title="What the record contains">
          <Box component="ul" sx={{ m: 0, pl: 3, display: "grid", gap: 0.75 }}>
            {CONTENTS.map((c) => (
              <Typography component="li" key={c}>
                {c}
              </Typography>
            ))}
          </Box>
        </Section>

        <Section id="record-check" title="Checking a copy">
          <Stack spacing={1.5}>
            <Typography>
              The SHA-256 fingerprint is printed on every page of the PDF and stored in the JSON file. Two copies with the
              same fingerprint hold exactly the same record; it changes only when the student's own data changes.
            </Typography>
            <Typography>
              Related:{" "}
              <Link component={RouterLink} to={`/admin/compliance/${student._id}`}>
                compliance record
              </Link>
              ,{" "}
              <Link component={RouterLink} to={`/admin/exports/daily-hours?userId=${student._id}`}>
                learning time per day
              </Link>{" "}
              and{" "}
              <Link component={RouterLink} to="/admin/exports">
                every student's records
              </Link>
              .
            </Typography>
          </Stack>
        </Section>
      </Box>
    </Stack>
  );
}
